import { ApiClientError, NetworkError } from '@recipes/api-client';
import {
  CATALOG_MAX_PRODUCT_PHOTOS,
  type LoyaltyCard,
  type LoyaltyCardUpdateInput,
  type LoyaltyCardWriteRequest,
  type PriceObservation,
  type PriceUpdateInput,
  type PriceWriteRequest,
  type Product,
  type ProductUpdateInput,
  type ProductWriteRequest,
  type PurchaseHint,
  type PurchaseHintUpdateInput,
  type PurchaseHintWriteRequest,
  type Store,
  type StoreUpdateInput,
  type StoreWriteRequest,
} from '@recipes/contracts';
import { api } from '@/lib/api';
import { authStore } from '@/lib/auth/auth-store';
import { offlineDb, type PendingCatalogAction, type PendingCatalogPhoto } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';
import { computeUnitPrice } from '@recipes/domain';
import { notifyCatalog, toHintRow, toPriceRow, toProductRow } from './device-copy';

/**
 * Local-first writes for the catalog (docs/finished/ingredient-catalog-spec.md 6.9): every change is
 * applied to the device copy and queued in one transaction, then replayed in order. Creates
 * carry a client-generated id that doubles as idempotency key, so hints written against a
 * product captured offline keep pointing at it. Nothing is dropped on 401 or network errors.
 */

export interface CatalogReplayResult {
  done: number;
  failed: number;
  offline: boolean;
}

const EMPTY_SUMMARY = { recommended: 0, avoid: 0, note: 0, averageRating: null };

let running: Promise<CatalogReplayResult> | null = null;

function isPermanent(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 401 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

async function enqueue(action: Omit<PendingCatalogAction, 'id' | 'createdAt' | 'attempts'>) {
  await offlineDb.pendingCatalog.add({ ...action, createdAt: Date.now(), attempts: 0 });
}

async function uploadPhoto(photo: PendingCatalogPhoto): Promise<string> {
  const intent = await api.media.createUploadIntent({
    fileName: photo.name || 'product.jpg',
    mimeType: photo.mimeType || 'image/jpeg',
    sizeBytes: photo.blob.size,
    purpose: photo.purpose ?? 'PRODUCT',
  });
  try {
    await api.media.upload(intent, photo.blob);
  } catch (error) {
    throw error instanceof TypeError ? new NetworkError(error) : error;
  }
  return (await api.media.complete(intent.assetId, { altText: null })).id;
}

/** A queued product or store that the server answered with another id: re-point what follows. */
async function repoint(kind: 'product' | 'store', from: string, to: string): Promise<void> {
  if (from === to) return;
  const rest = await offlineDb.pendingCatalog.toArray();
  for (const action of rest) {
    const payload = action.payload as Record<string, unknown>;
    const key = kind === 'product' ? 'productId' : 'storeId';
    let changed = false;
    if (payload?.[key] === from) {
      payload[key] = to;
      changed = true;
    }
    if (kind === 'product' && action.entityId === from && action.kind === 'product-update') {
      action.entityId = to;
      changed = true;
    }
    if (changed) await offlineDb.pendingCatalog.put(action);
  }
  if (kind === 'product') {
    await offlineDb.catalogHints
      .where('productId')
      .equals(from)
      .modify((row) => {
        row.productId = to;
        row.hint = { ...row.hint, productId: to };
      });
    await offlineDb.catalogPrices
      .where('productId')
      .equals(from)
      .modify((row) => {
        row.productId = to;
        row.price = { ...row.price, productId: to };
      });
    await offlineDb.catalogProducts.delete(from);
    const thumb = await offlineDb.catalogThumbs.get(from);
    if (thumb) {
      await offlineDb.catalogThumbs.delete(from);
      await offlineDb.catalogThumbs.put({ ...thumb, productId: to });
    }
  } else {
    await offlineDb.catalogHints
      .where('storeId')
      .equals(from)
      .modify((row) => {
        row.storeId = to;
        row.hint = { ...row.hint, storeId: to };
      });
    await offlineDb.catalogPrices
      .where('storeId')
      .equals(from)
      .modify((row) => {
        row.storeId = to;
        row.price = { ...row.price, storeId: to };
      });
    await offlineDb.catalogStores.delete(from);
  }
}

async function runAction(action: PendingCatalogAction): Promise<void> {
  switch (action.kind) {
    case 'product-create': {
      const photos = action.photos ?? [];
      for (const photo of photos) {
        if (photo.assetId) continue;
        photo.assetId = await uploadPhoto(photo);
        await offlineDb.pendingCatalog.put(action);
      }
      const body = action.payload as ProductWriteRequest;
      const product = await api.products.create(
        {
          ...body,
          id: action.entityId,
          photos: photos.flatMap((p) => (p.assetId ? [{ assetId: p.assetId, role: p.role }] : [])),
        },
        action.entityId,
      );
      // Someone captured this barcode meanwhile: the server merged us into their product.
      await repoint('product', action.entityId, product.id);
      await offlineDb.catalogProducts.put(toProductRow(product, 'sync'));
      return;
    }
    case 'product-update': {
      // Photos added on this device: uploaded first (each once), then appended to the ones the
      // product keeps.
      const added = action.photos ?? [];
      for (const photo of added) {
        if (photo.assetId) continue;
        photo.assetId = await uploadPhoto(photo);
        await offlineDb.pendingCatalog.put(action);
      }
      const stored = action.payload as ProductUpdateInput;
      const payload: ProductUpdateInput = added.length
        ? {
            ...stored,
            photos: [
              ...(stored.photos ?? []),
              ...added.flatMap((p) => (p.assetId ? [{ assetId: p.assetId, role: p.role }] : [])),
            ].slice(0, CATALOG_MAX_PRODUCT_PHOTOS),
          }
        : stored;
      let product;
      try {
        product = await api.products.update(action.entityId, payload);
      } catch (error) {
        // The version moved on – by someone else, or by this very change when the page was
        // closed before the queue entry could be removed. What the user set offline is applied
        // on top of the current state (last write wins per field), so a replay is harmless.
        if (!(error instanceof ApiClientError && error.status === 409)) throw error;
        const current = await api.products.get(action.entityId);
        product = await api.products.update(action.entityId, {
          ...payload,
          version: current.version,
        });
      }
      // Correcting a global row answers with the household's shadow (a different id).
      if (product.id !== action.entityId) await repoint('product', action.entityId, product.id);
      await offlineDb.catalogProducts.put(toProductRow(product, 'sync'));
      return;
    }
    case 'store-update': {
      const payload = action.payload as StoreUpdateInput;
      let store;
      try {
        store = await api.stores.update(action.entityId, payload);
      } catch (error) {
        // Someone changed the store meanwhile: what was set here goes on top of it.
        if (!(error instanceof ApiClientError && error.status === 409)) throw error;
        const current = await api.stores.get(action.entityId);
        store = await api.stores.update(action.entityId, { ...payload, version: current.version });
      }
      await offlineDb.catalogStores.put({ id: store.id, store });
      return;
    }
    case 'store-create': {
      const store = await api.stores.create({
        ...(action.payload as StoreWriteRequest),
        id: action.entityId,
      });
      await repoint('store', action.entityId, store.id);
      await offlineDb.catalogStores.put({ id: store.id, store });
      return;
    }
    case 'hint-create': {
      const hint = await api.purchaseHints.create(
        { ...(action.payload as PurchaseHintWriteRequest), id: action.entityId },
        action.entityId,
      );
      await offlineDb.catalogHints.put(toHintRow(hint));
      return;
    }
    case 'hint-update': {
      const hint = await api.purchaseHints.update(
        action.entityId,
        action.payload as PurchaseHintUpdateInput,
      );
      await offlineDb.catalogHints.put(toHintRow(hint));
      return;
    }
    case 'price-create': {
      const price = await api.prices.create(
        { ...(action.payload as PriceWriteRequest), id: action.entityId },
        action.entityId,
      );
      await offlineDb.catalogPrices.put(toPriceRow(price));
      return;
    }
    case 'price-update': {
      const payload = action.payload as PriceUpdateInput;
      let price;
      try {
        price = await api.prices.update(action.entityId, payload);
      } catch (error) {
        // The version moved on: what was typed here is the newer observation of the two.
        if (!(error instanceof ApiClientError && error.status === 409)) throw error;
        const current = (error.error.details as { currentVersion?: number } | undefined)
          ?.currentVersion;
        if (current === undefined) throw error;
        price = await api.prices.update(action.entityId, { ...payload, version: current });
      }
      await offlineDb.catalogPrices.put(toPriceRow(price));
      return;
    }
    case 'price-delete': {
      try {
        await api.prices.remove(action.entityId);
      } catch (error) {
        if (!(error instanceof ApiClientError && error.status === 404)) throw error;
      }
      return;
    }
    case 'hint-delete': {
      try {
        await api.purchaseHints.remove(action.entityId);
      } catch (error) {
        // Already gone on the server is what we wanted.
        if (!(error instanceof ApiClientError && error.status === 404)) throw error;
      }
      return;
    }
    case 'loyalty-card-create':
    case 'loyalty-card-update': {
      // The picture taken on this device is uploaded first (once), then the card is written.
      const photo = action.photos?.[0];
      if (photo && !photo.assetId) {
        photo.assetId = await uploadPhoto(photo);
        await offlineDb.pendingCatalog.put(action);
      }
      const imageAssetId = photo?.assetId ?? undefined;
      let card: LoyaltyCard;
      if (action.kind === 'loyalty-card-create') {
        card = await api.loyaltyCards.create({
          ...(action.payload as LoyaltyCardWriteRequest),
          id: action.entityId,
          ...(imageAssetId ? { imageAssetId } : {}),
        });
      } else {
        const payload = {
          ...(action.payload as LoyaltyCardUpdateInput),
          ...(imageAssetId ? { imageAssetId } : {}),
        };
        try {
          card = await api.loyaltyCards.update(action.entityId, payload);
        } catch (error) {
          // Someone changed the card meanwhile: what was set here goes on top of it.
          if (!(error instanceof ApiClientError && error.status === 409)) throw error;
          const current = await api.loyaltyCards.get(action.entityId);
          card = await api.loyaltyCards.update(action.entityId, {
            ...payload,
            version: current.version,
          });
        }
      }
      await offlineDb.transaction(
        'rw',
        [offlineDb.catalogLoyaltyCards, offlineDb.catalogCardImages],
        async () => {
          await offlineDb.catalogLoyaltyCards.put({ id: card.id, card });
          // The local picture now has its asset: the sync will not fetch it again.
          const image = await offlineDb.catalogCardImages.get(card.id);
          if (image && image.assetId === null && card.image)
            await offlineDb.catalogCardImages.put({ ...image, assetId: card.image.assetId });
        },
      );
      return;
    }
    case 'loyalty-card-delete': {
      try {
        await api.loyaltyCards.remove(action.entityId);
      } catch (error) {
        if (!(error instanceof ApiClientError && error.status === 404)) throw error;
      }
      return;
    }
  }
}

/** Replays the queue in order. Stops when the network or the session is not there. */
export function replayCatalogQueue(): Promise<CatalogReplayResult> {
  if (running) return running;
  running = (async () => {
    const result: CatalogReplayResult = { done: 0, failed: 0, offline: false };
    const queue = await offlineDb.pendingCatalog.orderBy('id').toArray();
    for (const queued of queue) {
      // Re-read: an earlier action may have re-pointed this one.
      const action = (await offlineDb.pendingCatalog.get(queued.id!)) ?? queued;
      try {
        await runAction(action);
        await offlineDb.pendingCatalog.delete(action.id!);
        result.done++;
      } catch (error) {
        if (
          error instanceof NetworkError ||
          (error instanceof ApiClientError && error.status === 401)
        ) {
          result.offline = true;
          break;
        }
        await offlineDb.pendingCatalog.update(action.id!, {
          attempts: action.attempts + 1,
          lastError: error instanceof Error ? error.message : String(error),
        });
        result.failed++;
        // A rejected change stays visible with its error; later actions may depend on it.
        if (!isPermanent(error)) break;
      }
    }
    if (result.done > 0) notifyCatalog();
    return result;
  })().finally(() => {
    running = null;
  });
  return running;
}

async function replayIfOnline(): Promise<void> {
  const platform = await getPlatform();
  if (platform.isOnline()) await replayCatalogQueue();
}

const now = () => new Date().toISOString();
const me = () => authStore.getState().user;

// ---------------------------------------------------------------------------
// Local-first operations
// ---------------------------------------------------------------------------

export interface HintDraft {
  householdId?: string | null;
  ingredientId?: string | null;
  productId?: string | null;
  storeId?: string | null;
  verdict: PurchaseHint['verdict'];
  rating?: number | null;
  text: string;
  /** Names for the local copy; the server fills them in on sync. */
  storeName?: string | null;
  targetName?: string | null;
}

export async function saveHint(draft: HintDraft): Promise<PurchaseHint> {
  const id = crypto.randomUUID();
  const { storeName, targetName, ...payload } = draft;
  const hint: PurchaseHint = {
    id,
    householdId: draft.householdId ?? null,
    ingredientId: draft.ingredientId ?? null,
    productId: draft.productId ?? null,
    storeId: draft.storeId ?? null,
    storeName: storeName ?? null,
    targetName: targetName ?? null,
    verdict: draft.verdict,
    rating: draft.rating ?? null,
    text: draft.text,
    authorId: me()?.id ?? null,
    authorName: me()?.displayName ?? null,
    own: true,
    createdAt: now(),
    updatedAt: now(),
  };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogHints, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogHints.put(toHintRow(hint, true));
      await enqueue({ kind: 'hint-create', entityId: id, payload });
    },
  );
  notifyCatalog();
  await replayIfOnline();
  return (await offlineDb.catalogHints.get(id))?.hint ?? hint;
}

export async function updateHint(
  hint: PurchaseHint,
  patch: PurchaseHintUpdateInput & { storeName?: string | null },
): Promise<void> {
  const { storeName, ...payload } = patch;
  const next: PurchaseHint = {
    ...hint,
    ...payload,
    storeName: patch.storeId === undefined ? hint.storeName : (storeName ?? null),
    updatedAt: now(),
  } as PurchaseHint;
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogHints, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogHints.put(toHintRow(next, true));
      await enqueue({ kind: 'hint-update', entityId: hint.id, payload });
    },
  );
  notifyCatalog();
  await replayIfOnline();
}

export async function deleteHint(hintId: string): Promise<void> {
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogHints, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogHints.delete(hintId);
      // A hint that never reached the server simply disappears together with its create.
      const creates = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(hintId)
        .filter((a) => a.kind === 'hint-create')
        .toArray();
      if (creates.length) {
        await offlineDb.pendingCatalog.where('entityId').equals(hintId).delete();
      } else {
        await enqueue({ kind: 'hint-delete', entityId: hintId, payload: {} });
      }
    },
  );
  notifyCatalog();
  await replayIfOnline();
}

export interface ProductCapture {
  body: Omit<ProductWriteRequest, 'id' | 'photos'>;
  photos: Array<{ file: Blob; name: string; role: 'FRONT' | 'LABEL' }>;
  ingredientName?: string | null;
}

/** Captures a product on the device; usable right away, completed on the server later. */
export async function captureProduct(capture: ProductCapture): Promise<Product> {
  const id = crypto.randomUUID();
  const body = capture.body;
  const local: Product = {
    id,
    scope: body.householdId === null ? 'USER' : 'HOUSEHOLD',
    householdId: body.householdId ?? null,
    barcode: body.barcode ?? null,
    brand: body.brand ?? null,
    name: body.name,
    ingredient: null,
    packageQuantity: body.packageQuantity ?? null,
    packageUnitCode: body.packageUnitCode ?? null,
    packageUnitText: body.packageUnitText ?? null,
    ingredientsText: body.ingredientsText ?? null,
    allergens: body.allergens ?? [],
    additives: body.additives ?? [],
    labels: body.labels ?? [],
    nutriScore: body.nutriScoreNone ? null : (body.nutriScore ?? null),
    nutriScoreSource: body.nutriScore || body.nutriScoreNone ? 'USER' : null,
    nutriScoreNone: Boolean(body.nutriScoreNone),
    novaGroup: body.novaGroup ?? null,
    ecoScore: null,
    nutrition: null,
    source: 'USER',
    attribution: null,
    photos: [],
    shadowsProductId: null,
    hintSummary: EMPTY_SUMMARY,
    editable: true,
    version: 0,
    fetchedAt: null,
    createdAt: now(),
    updatedAt: now(),
  };
  if (body.ingredientId) {
    const row = await offlineDb.catalogIngredients.get(body.ingredientId);
    if (row) {
      const {
        id: ingredientId,
        scope,
        name,
        names,
        normalizedName,
        groceryCategory,
      } = row.ingredient;
      local.ingredient = { id: ingredientId, scope, name, names, normalizedName, groceryCategory };
    }
  }
  const front = capture.photos.find((p) => p.role === 'FRONT');
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogProducts, offlineDb.catalogThumbs, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogProducts.put(toProductRow(local, 'local'));
      if (front)
        await offlineDb.catalogThumbs.put({ productId: id, blob: front.file, savedAt: Date.now() });
      await enqueue({
        kind: 'product-create',
        entityId: id,
        payload: body,
        photos: capture.photos.map((p) => ({
          blob: p.file,
          name: p.name,
          mimeType: p.file.type || 'image/jpeg',
          role: p.role,
          assetId: null,
        })),
      });
    },
  );
  notifyCatalog();
  await replayIfOnline();
  // After a successful replay the server's row (possibly another id) replaced the local one.
  const stored = await offlineDb.catalogProducts.get(id);
  if (stored) return stored.product;
  const byBarcode = body.barcode
    ? await offlineDb.catalogProducts.where('barcode').equals(body.barcode).first()
    : undefined;
  return byBarcode?.product ?? local;
}

/** Patches a product locally and queues the change (Nutri-Score by hand, corrections). */
export async function patchProduct(
  product: Product,
  patch: Omit<ProductUpdateInput, 'version'>,
): Promise<Product> {
  const linked =
    patch.ingredientId === undefined || patch.ingredientId === product.ingredient?.id
      ? undefined
      : patch.ingredientId
        ? ((await offlineDb.catalogIngredients.get(patch.ingredientId))?.ingredient ?? null)
        : null;
  const next: Product = {
    ...product,
    ...(linked === undefined
      ? {}
      : {
          ingredient: linked && {
            id: linked.id,
            scope: linked.scope,
            name: linked.name,
            names: linked.names,
            normalizedName: linked.normalizedName,
            groceryCategory: linked.groceryCategory,
          },
        }),
    ...('nutriScore' in patch || 'nutriScoreNone' in patch
      ? {
          nutriScore: patch.nutriScoreNone ? null : (patch.nutriScore ?? null),
          nutriScoreNone: Boolean(patch.nutriScoreNone),
          nutriScoreSource: patch.nutriScore || patch.nutriScoreNone ? ('USER' as const) : null,
        }
      : {}),
    ...(patch.name !== undefined ? { name: patch.name } : {}),
    ...(patch.brand !== undefined ? { brand: patch.brand ?? null } : {}),
    ...(patch.photos !== undefined
      ? {
          photos: patch.photos.flatMap((kept) =>
            product.photos.filter((photo) => photo.assetId === kept.assetId),
          ),
        }
      : {}),
    updatedAt: now(),
  };
  const row = await offlineDb.catalogProducts.get(product.id);
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogProducts, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogProducts.put({
        ...toProductRow(next, row?.origin ?? 'lookup'),
      });
      const creating = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(product.id)
        .filter((a) => a.kind === 'product-create')
        .first();
      if (creating) {
        // Not on the server yet: fold the change into the create.
        creating.payload = { ...(creating.payload as object), ...patch };
        await offlineDb.pendingCatalog.put(creating);
      } else {
        await enqueue({
          kind: 'product-update',
          entityId: product.id,
          payload: { ...patch, version: product.version },
        });
      }
    },
  );
  notifyCatalog();
  await replayIfOnline();
  const stored = await offlineDb.catalogProducts.get(product.id);
  if (stored) return stored.product;
  const shadow = product.barcode
    ? await offlineDb.catalogProducts
        .where('barcode')
        .equals(product.barcode)
        .filter((r) => r.product.scope !== 'GLOBAL')
        .first()
    : undefined;
  return shadow?.product ?? next;
}

/**
 * Adds a photo to a product that exists already. The photo waits in the queue with the change
 * and is uploaded by the replay; until then the device shows it from `catalogThumbs` when the
 * product had no picture at all.
 */
export async function addProductPhoto(
  product: Product,
  photo: { file: Blob; name: string; role: 'FRONT' | 'LABEL' },
): Promise<void> {
  if (product.photos.length >= CATALOG_MAX_PRODUCT_PHOTOS) return;
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogProducts, offlineDb.catalogThumbs, offlineDb.pendingCatalog],
    async () => {
      if (product.photos.length === 0)
        await offlineDb.catalogThumbs.put({
          productId: product.id,
          blob: photo.file,
          savedAt: Date.now(),
        });
      const pendingPhoto = {
        blob: photo.file,
        name: photo.name,
        mimeType: photo.file.type || 'image/jpeg',
        role: photo.role,
        assetId: null,
      };
      const creating = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(product.id)
        .filter((a) => a.kind === 'product-create')
        .first();
      if (creating) {
        creating.photos = [...(creating.photos ?? []), pendingPhoto];
        await offlineDb.pendingCatalog.put(creating);
      } else {
        await enqueue({
          kind: 'product-update',
          entityId: product.id,
          payload: {
            photos: product.photos.map((p) => ({ assetId: p.assetId, role: p.role })),
            version: product.version,
          },
          photos: [pendingPhoto],
        });
      }
    },
  );
  notifyCatalog();
  await replayIfOnline();
}

/** Changes a store locally and queues the change (department order, rating, comment, loyalty card). */
export async function updateStoreLocal(
  store: Store,
  patch: Pick<StoreUpdateInput, 'departmentOrder' | 'rating' | 'note' | 'loyaltyCardId'>,
): Promise<Store> {
  const next: Store = { ...store, ...patch, updatedAt: now() };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogStores, offlineDb.pendingCatalog],
    async () => {
      const row = await offlineDb.catalogStores.get(store.id);
      await offlineDb.catalogStores.put({ ...row, id: store.id, store: next });
      const creating = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(store.id)
        .filter((a) => a.kind === 'store-create')
        .first();
      if (creating) {
        creating.payload = { ...(creating.payload as object), ...patch };
        await offlineDb.pendingCatalog.put(creating);
      } else {
        await enqueue({
          kind: 'store-update',
          entityId: store.id,
          payload: { ...patch, version: store.version },
        });
      }
    },
  );
  notifyCatalog();
  await replayIfOnline();
  return (await offlineDb.catalogStores.get(store.id))?.store ?? next;
}

export async function createStoreLocal(body: StoreWriteRequest): Promise<Store> {
  const id = crypto.randomUUID();
  const store: Store = {
    id,
    householdId: body.householdId ?? null,
    kind: body.kind ?? 'CHAIN',
    parentStoreId: body.parentStoreId ?? null,
    parentName: body.chainName ?? null,
    name: body.name,
    lat: body.lat ?? null,
    lng: body.lng ?? null,
    street: body.street ?? null,
    postalCode: body.postalCode ?? null,
    city: body.city ?? null,
    countryCode: body.countryCode ?? null,
    provider: body.provider ?? null,
    providerRef: body.providerRef ?? null,
    note: body.note ?? null,
    rating: body.rating ?? null,
    loyaltyCardId: body.loyaltyCardId ?? null,
    hintCount: 0,
    branchCount: 0,
    editable: true,
    version: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogStores, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogStores.put({ id, store, pending: true });
      await enqueue({ kind: 'store-create', entityId: id, payload: body });
    },
  );
  notifyCatalog();
  await replayIfOnline();
  const stored = await offlineDb.catalogStores.get(id);
  if (stored) return stored.store;
  // The server reused an existing store (same OSM element): it replaced ours under its id.
  const reused = await offlineDb.catalogStores
    .filter((r) =>
      body.providerRef ? r.store.providerRef === body.providerRef : r.store.name === body.name,
    )
    .first();
  return reused?.store ?? store;
}

/**
 * Deletes a store on the server and drops it from the device copy at once. The delta sync
 * alone is not enough here: the server only delivers changes older than a second, so the
 * tombstone written by this delete would arrive with the next sync – after a reload or when
 * the app comes back (user: "after deleting a store the list is not updated"). Mirrors what
 * the server does: branches lose their parent, prices lose the store, hints lose the store
 * (`keep`) or go with it (`delete`).
 */
export async function removeStore(id: string, hints: 'keep' | 'delete'): Promise<void> {
  await api.stores.remove(id, hints);
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogStores, offlineDb.catalogHints, offlineDb.catalogPrices],
    async () => {
      if (hints === 'delete') {
        await offlineDb.catalogHints.where('storeId').equals(id).delete();
      } else {
        await offlineDb.catalogHints
          .where('storeId')
          .equals(id)
          .modify((row) => {
            row.storeId = null;
            row.hint = { ...row.hint, storeId: null, storeName: null };
          });
      }
      await offlineDb.catalogPrices
        .where('storeId')
        .equals(id)
        .modify((row) => {
          row.storeId = null;
          row.price = { ...row.price, storeId: null };
        });
      await offlineDb.catalogPrices
        .where('chainId')
        .equals(id)
        .modify((row) => {
          row.chainId = null;
          row.price = { ...row.price, chainId: null };
        });
      await offlineDb.catalogStores
        .filter((row) => row.store.parentStoreId === id)
        .modify((row) => {
          row.store = { ...row.store, parentStoreId: null, parentName: null };
        });
      await offlineDb.catalogStores.delete(id);
    },
  );
  notifyCatalog();
}

/** Deletes a product on the server and drops it from the device copy at once (see `removeStore`). */
export async function removeProduct(id: string): Promise<void> {
  await api.products.remove(id);
  await offlineDb.transaction(
    'rw',
    [
      offlineDb.catalogProducts,
      offlineDb.catalogHints,
      offlineDb.catalogPrices,
      offlineDb.catalogThumbs,
    ],
    async () => {
      // The server cascades hints to the product and unlinks its prices.
      await offlineDb.catalogHints.where('productId').equals(id).delete();
      await offlineDb.catalogPrices
        .where('productId')
        .equals(id)
        .modify((row) => {
          row.productId = null;
          row.price = { ...row.price, productId: null };
        });
      await offlineDb.catalogThumbs.delete(id);
      await offlineDb.catalogProducts.delete(id);
    },
  );
  notifyCatalog();
}

// ---------------------------------------------------------------------------
// Loyalty cards (user, 2026-09-25: Payback)
// ---------------------------------------------------------------------------

export interface LoyaltyCardDraft {
  name: string;
  /** A picture of the card taken or chosen on this device. */
  image?: { file: Blob; name: string } | null;
}

const pendingCardPhoto = (image: { file: Blob; name: string }): PendingCatalogPhoto => ({
  blob: image.file,
  name: image.name,
  mimeType: image.file.type || 'image/jpeg',
  role: 'FRONT',
  purpose: 'LOYALTY_CARD',
  assetId: null,
});

/**
 * Creates a card locally and queues it: the picture is kept on the device at once (it is
 * what the checkout needs), uploaded by the replay, and the card written after it.
 */
export async function createLoyaltyCardLocal(draft: LoyaltyCardDraft): Promise<LoyaltyCard> {
  const id = crypto.randomUUID();
  const card: LoyaltyCard = {
    id,
    householdId: null,
    name: draft.name,
    image: null,
    storeIds: [],
    editable: true,
    version: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogLoyaltyCards, offlineDb.catalogCardImages, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogLoyaltyCards.put({ id, card, pending: true });
      if (draft.image)
        await offlineDb.catalogCardImages.put({
          cardId: id,
          assetId: null,
          blob: draft.image.file,
          savedAt: Date.now(),
        });
      await enqueue({
        kind: 'loyalty-card-create',
        entityId: id,
        payload: { name: draft.name } satisfies LoyaltyCardWriteRequest,
        photos: draft.image ? [pendingCardPhoto(draft.image)] : [],
      });
    },
  );
  notifyCatalog();
  await replayIfOnline();
  return (await offlineDb.catalogLoyaltyCards.get(id))?.card ?? card;
}

/** Renames a card or gives it a new picture; `image: null` leaves the picture as it is. */
export async function updateLoyaltyCardLocal(
  card: LoyaltyCard,
  draft: LoyaltyCardDraft,
): Promise<LoyaltyCard> {
  const next: LoyaltyCard = { ...card, name: draft.name, updatedAt: now() };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogLoyaltyCards, offlineDb.catalogCardImages, offlineDb.pendingCatalog],
    async () => {
      const row = await offlineDb.catalogLoyaltyCards.get(card.id);
      await offlineDb.catalogLoyaltyCards.put({ ...row, id: card.id, card: next });
      if (draft.image)
        await offlineDb.catalogCardImages.put({
          cardId: card.id,
          assetId: null,
          blob: draft.image.file,
          savedAt: Date.now(),
        });
      const creating = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(card.id)
        .filter((a) => a.kind === 'loyalty-card-create')
        .first();
      if (creating) {
        creating.payload = { ...(creating.payload as object), name: draft.name };
        if (draft.image) creating.photos = [pendingCardPhoto(draft.image)];
        await offlineDb.pendingCatalog.put(creating);
      } else {
        await enqueue({
          kind: 'loyalty-card-update',
          entityId: card.id,
          payload: { name: draft.name, version: card.version } satisfies LoyaltyCardUpdateInput,
          photos: draft.image ? [pendingCardPhoto(draft.image)] : [],
        });
      }
    },
  );
  notifyCatalog();
  await replayIfOnline();
  return (await offlineDb.catalogLoyaltyCards.get(card.id))?.card ?? next;
}

/** Deletes a card: gone from the device at once, the stores that showed it lose it, the server follows. */
export async function removeLoyaltyCard(id: string): Promise<void> {
  await offlineDb.transaction(
    'rw',
    [
      offlineDb.catalogLoyaltyCards,
      offlineDb.catalogCardImages,
      offlineDb.catalogStores,
      offlineDb.pendingCatalog,
    ],
    async () => {
      await offlineDb.catalogLoyaltyCards.delete(id);
      await offlineDb.catalogCardImages.delete(id);
      await offlineDb.catalogStores
        .filter((row) => row.store.loyaltyCardId === id)
        .modify((row) => {
          row.store = { ...row.store, loyaltyCardId: null };
        });
      // A card that never reached the server is simply forgotten.
      const creating = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(id)
        .filter((a) => a.kind === 'loyalty-card-create' || a.kind === 'loyalty-card-update')
        .toArray();
      const wasLocal = creating.some((a) => a.kind === 'loyalty-card-create');
      for (const action of creating) await offlineDb.pendingCatalog.delete(action.id!);
      if (!wasLocal) await enqueue({ kind: 'loyalty-card-delete', entityId: id, payload: {} });
    },
  );
  notifyCatalog();
  await replayIfOnline();
}

export async function pendingCatalogActions(): Promise<PendingCatalogAction[]> {
  return offlineDb.pendingCatalog.orderBy('id').toArray();
}

export async function discardCatalogAction(id: number): Promise<void> {
  await offlineDb.pendingCatalog.delete(id);
  notifyCatalog();
}

// ---------------------------------------------------------------------------
// Prices (docs/partial/shopping-in-store-spec.md 6.3): typing a price never needs a connection
// ---------------------------------------------------------------------------

export interface PriceDraft {
  productId?: string | null;
  ingredientId?: string | null;
  storeId: string | null;
  /** The chain that answers as fallback: the branch's parent, or the store itself. */
  chainId: string | null;
  price: string;
  currency: string;
  perQuantity?: string | null;
  perUnitCode?: string | null;
  /** The product's package, for the unit price of the local copy. */
  packageQuantity?: string | null;
  packageUnitCode?: string | null;
  isPromotion?: boolean;
  promotionEndsAt?: string | null;
  depositAmount?: string | null;
  observedAt: string;
  note?: string | null;
  /** Confirms a price tag scan; needs a connection, so it is never queued for long. */
  tagScanId?: string;
}

export async function savePrice(draft: PriceDraft): Promise<PriceObservation> {
  const id = crypto.randomUUID();
  const unit =
    draft.perQuantity && draft.perUnitCode
      ? computeUnitPrice({
          price: draft.price,
          quantity: draft.perQuantity,
          unitCode: draft.perUnitCode,
        })
      : computeUnitPrice({
          price: draft.price,
          quantity: draft.packageQuantity ?? null,
          unitCode: draft.packageUnitCode ?? null,
        });
  const price: PriceObservation = {
    id,
    householdId: null,
    productId: draft.productId ?? null,
    ingredientId: draft.productId ? null : (draft.ingredientId ?? null),
    storeId: draft.storeId,
    chainId: draft.chainId,
    price: draft.price,
    currency: draft.currency,
    perQuantity: draft.perQuantity ?? null,
    perUnitCode: draft.perQuantity ? (draft.perUnitCode ?? null) : null,
    packageQuantity: draft.packageQuantity ?? null,
    packageUnitCode: draft.packageUnitCode ?? null,
    unitPrice: unit?.unitPrice ?? null,
    unitPriceBase: unit?.unitPriceBase ?? null,
    isPromotion: draft.isPromotion ?? false,
    promotionEndsAt: draft.promotionEndsAt ?? null,
    depositAmount: draft.depositAmount ?? null,
    source: draft.tagScanId ? 'PRICE_TAG' : 'MANUAL',
    receiptId: null,
    observedAt: draft.observedAt,
    note: draft.note ?? null,
    createdById: me()?.id ?? null,
    createdByName: me()?.displayName ?? null,
    editable: true,
    version: 0,
    createdAt: now(),
    updatedAt: now(),
  };
  const payload: PriceWriteRequest = {
    productId: price.productId,
    ingredientId: price.ingredientId,
    storeId: price.storeId,
    price: price.price,
    currency: price.currency,
    perQuantity: price.perQuantity,
    perUnitCode: price.perUnitCode,
    isPromotion: price.isPromotion,
    promotionEndsAt: price.promotionEndsAt,
    depositAmount: price.depositAmount,
    observedAt: price.observedAt,
    note: price.note,
    ...(draft.tagScanId ? { tagScanId: draft.tagScanId } : {}),
  };
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogPrices, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogPrices.put(toPriceRow(price, true));
      await enqueue({ kind: 'price-create', entityId: id, payload });
    },
  );
  notifyCatalog();
  await replayIfOnline();
  return (await offlineDb.catalogPrices.get(id))?.price ?? price;
}

export async function removePrice(price: PriceObservation): Promise<void> {
  await offlineDb.transaction(
    'rw',
    [offlineDb.catalogPrices, offlineDb.pendingCatalog],
    async () => {
      await offlineDb.catalogPrices.delete(price.id);
      // Typed and deleted again before it was sent: nothing to tell the server.
      const created = await offlineDb.pendingCatalog
        .where('entityId')
        .equals(price.id)
        .filter((action) => action.kind === 'price-create')
        .first();
      if (created) await offlineDb.pendingCatalog.delete(created.id!);
      else await enqueue({ kind: 'price-delete', entityId: price.id, payload: {} });
    },
  );
  notifyCatalog();
  await replayIfOnline();
}
